import { ref } from 'vue';
import { checkAssetsDirExists, D3_BASE_PATH, getPlatformConfig } from '@/capacitor/file/constants';
import { Capacitor } from '@capacitor/core';
import { Filesystem } from '@capacitor/filesystem';
import { getKeyStr ,removeKey, setKey, setKeyJsonStr ,getKeyJson,updateKeyJson,getsubKey, removeKeys} from 'd3components/src/utils/localStorage';

import { useUserStore } from 'd3components';

// 图片 URL、展示时长与淡出时长
export interface AdPayload {
  // 图片来源
  imageUrl: string;
  duration: number;
  fadeDuration: number;
}

const { directory } = getPlatformConfig();

class AdService {
  // 当前待展示的广告数据（被 AdPage.vue 获取）
  private ad = ref<AdPayload | null>(null);
  readonly startupBlocking = ref(false);

  setStartupBlocking(blocking: boolean): void {
    this.startupBlocking.value = blocking;
  }

  // 首次启动：展示打包内置图片， 非首次：Web 直接远程 URL；原生优先使用持久化本地图片，不存在则下载
  async init(): Promise<void> {
    try {
      // 获取展示时长：优先取 setting.advertising_time（秒），无配置则默认 5s
      // 注意：此处不处理“跳过”逻辑，由 main.ts 的 needAd 开关控制是否调用 init
      const setting: any = getKeyJson('setting');
      const advertisingTime = setting?.advertising_time;
      const durationMs = (Number(advertisingTime) > 0) ? Math.floor(Number(advertisingTime) * 1000) : 5000;

      // 优先判断平台：Web 直接设置远程图并返回
      const platform = Capacitor.getPlatform();
      if (platform === 'web') {
        // 广告图片优先级：erpConfig.sa_login_ad > 本地兜底图片
        const userStore: any = useUserStore();
        const imageUrl = (userStore.erpConfig?.sa_login_ad && userStore.serUrl) ? `${userStore.serUrl}${userStore.erpConfig.sa_login_ad}` : new URL('../../assets/image.png', import.meta.url).href;
        console.log('adadadad',imageUrl)
        this.ad.value = { ...this.buildLocalAd(durationMs), imageUrl };
        return;
      }

      // 兜底展示null
      if (!this.ad.value) {
        this.ad.value = this.buildLocalAd(durationMs);
      }

      // 记录是否已使用本地文件
      let usedPersisted = false;
      try {
        const targetPath = `${D3_BASE_PATH}/assets/image.png`;
        await Filesystem.stat({ path: targetPath, directory });
        const uri = await Filesystem.getUri({ path: targetPath, directory });
        const imageUrl = Capacitor.convertFileSrc(uri.uri);
        this.ad.value = { ...this.buildLocalAd(durationMs), imageUrl };
        usedPersisted = true;
      } catch (_) {
        // 无本地文件使用兜底文件
      }

      // 首次启动且无本地缓存图，直接展示兜底图返回
      const isFirstLaunch = getsubKey('mobile', 'firstLaunch') !== 'true';
      if (isFirstLaunch && !usedPersisted) {
        this.ad.value = this.buildLocalAd(durationMs);
        return;
      }
      // 确保 `${D3_BASE_PATH}/assets/` 目录存在
      // 原生平台：检查本地缓存并尝试下载，race 5s 超时避免卡住
      const targetPath = `${D3_BASE_PATH}/assets/image.png`;
      // 广告图片：erpConfig.sa_login_ad，无则使用兜底图片
      const userStore: any = useUserStore();
      const remoteUrl = (userStore.erpConfig?.sa_login_ad && userStore.serUrl) ? `${userStore.serUrl}${userStore.erpConfig.sa_login_ad}` : '';
      // 原代码：
      // const cached = getsubKey('mobile', 'loginAdUrl');
      // const remoteUrl = typeof cached === 'string' && cached ? cached : 'https://cloud.d3inf.com/public/asstes/test.png';
      // 没有远程地址则直接使用兜底图片
      if (!remoteUrl) {
        return;
      }
      void Promise.race([
        (async () => {
          try {
            await checkAssetsDirExists();
            const base64 = await this.fetchAsBase64(remoteUrl);
            await Filesystem.writeFile({
              path: targetPath,
              directory,
              data: base64,
              recursive: true,
            });
            const uri = await Filesystem.getUri({ path: targetPath, directory });
            const imageUrl = Capacitor.convertFileSrc(uri.uri);
            this.ad.value = { ...this.buildLocalAd(durationMs), imageUrl };
            console.log('下载广告图片成功');
          } catch (downloadErr) {
            console.warn('下载广告图片失败，使用本地资源', downloadErr);
            try {
              const uri = await Filesystem.getUri({ path: targetPath, directory });
              const imageUrl = Capacitor.convertFileSrc(uri.uri);
              this.ad.value = { ...this.buildLocalAd(durationMs), imageUrl };
            } catch {
              this.ad.value = this.buildLocalAd(durationMs);
            }
          }
        })(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('AD_TIMEOUT')), 3000)),
      ]).catch(() => {});
    } catch (err) {
      // 任意错误都回退到内置资源
      console.warn('广告初始化失败，使用本地资源', err);
      // 如果 durationMs<=0 已提前 return；走到这里说明需要展示广告，兜底按 5 秒。
      this.ad.value = this.buildLocalAd(5000);
    }
  }

  // 取出当前广告并清空
  consume(): AdPayload | null {
    const current = this.ad.value;
    this.ad.value = null;
    return current;
  }

  hasPending(): boolean {
    return !!this.ad.value;
  }

  // 广告页在关闭/跳过时调用：标记首次启动已完成
  markFirstLaunch(): void {
    try {
      const mobile = getKeyJson('mobile') || {};
      (mobile as any).firstLaunch = 'true';
      setKeyJsonStr('mobile', mobile);
    } catch {}
  }

  // 重置当前广告状态
  reset(): void {
    this.ad.value = null;
  }

  // 打包资源图片为基础的广告页，动态获取失败可以用这个
  private buildLocalAd(duration: number): AdPayload {
    // mobile动态控制图片淡出时长
    return {
      imageUrl: new URL('../../assets/image.png', import.meta.url).href,
      duration,
      fadeDuration: 1000,
    };
  }
  // 将远程图片拉取并转换为 base64（去除 dataURL 前缀）
  private async fetchAsBase64(url: string): Promise<string> {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1] || '');
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
export const adService = new AdService();
